import { call, put, select } from 'redux-saga/effects';
import * as actions from './actions';
import axios from 'axios';
import { Base64 } from 'js-base64';
import { produce } from 'immer';
import { updateTotalCount } from 'store/helpers/tableAndToolbar/actions';
import { updateEnrichedSchemaFilters } from './actions';
import { sagas as eventSagas } from 'store/event';
import { getParams } from 'store/helpers/helperFunctions';
const { waitForGetSupportedEventTypes } = eventSagas;

const decodeExampleContent = schema => {
  try {
    if(schema.examplePayload && schema.examplePayload !== '') {
      schema.examplePayload = Base64.decode(schema.examplePayload);
    }
  } catch (ex) {
    schema.examplePayload = "";
  }
  return schema;
};

const apiRoot = `${window._env_?.REACT_APP_SCHEMA_API_PATH}`;

const getEnrichedSchemaApiRoot = (tenantId, businessContextId) =>
  `${apiRoot}tenants/${tenantId}/context/${businessContextId}`;

let isArchiveInProgress = false;

export const getEnrichedSchemasAsync = (
  tenantId,
  businessContextId,
  showActive,
  enrichmentProduct
) =>
  axios
    .get(
      `${getEnrichedSchemaApiRoot(
        tenantId,
        businessContextId
      )}/schema-enrichment/search?${typeof showActive === 'boolean' ? `active=${showActive}` : ``}${
        enrichmentProduct ? `&enrichmentProduct=${enrichmentProduct}` : ``
      }`
    )
    .then(response => response.data)
    .catch(err => {
      throw err;
    });

export const getPaginatedEnrichedSchemasAsync = (
  tenantId,
  businessContextId,
  urlParams,
  bodyParams
) => {
  return axios
    .post(
      `${getEnrichedSchemaApiRoot(tenantId, businessContextId)}/schema-enrichment/search?index=${
        urlParams.index
      }&pageSize=${urlParams.pageSize}`,
      bodyParams
    )
    .then(response => ({
      data: response.data,
      totalRecords: parseInt(response.headers['x-ag-totalcount'])
    }))
    .catch(err => {
      throw err;
    });
};

export const getEnrichedSchemaAsync = (tenantId, businessContextId, uuid) =>
  axios
    .get(
      `${getEnrichedSchemaApiRoot(
        tenantId,
        businessContextId
      )}/schema-enrichment/${uuid}`
    )
    .then(response => response.data)
    .catch(err => {
      throw err;
    });

export const getEnrichedSchemaKeysAsync = (tenantId, businessContextId) =>
  axios
    .get(
      `${getEnrichedSchemaApiRoot(
        tenantId,
        businessContextId
      )}/schema-enrichment/fields`
    )
    .then(response => response.data)
    .catch(err => {
      throw err;
    });

export const createEnrichedSchemaAsync = (
  tenantId,
  businessContextId,
  schema
) =>
  axios
    .post(
      `${getEnrichedSchemaApiRoot(
        tenantId,
        businessContextId
      )}/schema-enrichment/configuration`,
      schema
    )
    .then(response => response.data)
    .catch(err => {
      throw err;
    });

export const updateEnrichedSchemaAsync = (
  tenantId,
  businessContextId,
  schema,
  uuid
) =>
  axios
    .put(
      `${getEnrichedSchemaApiRoot(
        tenantId,
        businessContextId
      )}/schema-enrichment/configuration/${uuid}`,
      schema
    )
    .then(response => response.data)
    .catch(err => {
      throw err;
    });

export const activateEnrichedSchemaAsync = (
  tenantId,
  businessContextId,
  uuid
) =>
  axios
    .put(
      `${getEnrichedSchemaApiRoot(
        tenantId,
        businessContextId
      )}/schema-enrichment/configuration/activate/${uuid}`
    )
    .then(response => response.data)
    .catch(err => {
      throw err;
    });

export const deactivateEnrichedSchemaAsync = (
  tenantId,
  businessContextId,
  uuid
) =>
  axios
    .put(
      `${getEnrichedSchemaApiRoot(
        tenantId,
        businessContextId
      )}/schema-enrichment/configuration/deactivate/${uuid}`
    )
    .then(response => response.data)
    .catch(err => {
      throw err;
    });

const currentTenantId = store => store.user.selectedTenant.tenantId;
const currentBusinessContextId = store =>
  store.user.selectedBusinessContext.businessContextId;
const currentEnrichedSchema = store =>
  store.schema ? store.enrichedSchema.current : {};
const currentList = store =>
  store.enrichedSchema ? store.enrichedSchema.list : [];

const paginatedSchemaList = store =>
  store.enrichedSchema ? store.enrichedSchema.paginatedList : [];
const productNames = store => store.events.supportedEventTypes.productNames;
const productCategories = store =>
  store.events.supportedEventTypes.productCategories;
const pageNo = store =>
  store.enrichedSchema.filters ? store.enrichedSchema.filters?.pageNo : 0;
const searchInput = store =>
  store.enrichedSchema.filters ? store.enrichedSchema.filters?.searchInput : '';
const sortBy = store =>
  store.enrichedSchema.filters ? store.enrichedSchema.filters?.sortBy : 'schemaName';
const sortOrder = store =>
  store.enrichedSchema.filters ? store.enrichedSchema.filters?.sortOrder : 'ASC';
const rowsPerPage = store =>
  store.enrichedSchema.filters ? store.enrichedSchema.filters?.rowsPerPage : 10;
const displayActive = store =>
  store.enrichedSchema.filters ? store.enrichedSchema.filters?.displayActive : true;
  const filterProductNames = store =>
  store.enrichedSchema.filters ? store.enrichedSchema.filters?.filterProductNames : [];
const filterProductCategories = store =>
  store.enrichedSchema.filters ? store.enrichedSchema.filters?.filterProductCategories : [];
const totalCount = store =>
  store.enrichedSchema.filters ? store.enrichedSchema.filters?.totalCount : 0;

export function* getEnrichedSchemasIfNeeded(action) {
  try {
    let tenantId = yield select(currentTenantId);
    let businessContextId = yield select(currentBusinessContextId);
    let list = yield select(currentList);
    let currentEnrichedSchemaList = list;
    if (action.payload.force || list.length === 0) {
      currentEnrichedSchemaList = yield call(
        getEnrichedSchemasAsync,
        tenantId,
        businessContextId,
        action.payload?.showActive,
        action.payload?.enrichmentProduct
      );
      currentEnrichedSchemaList = currentEnrichedSchemaList.map(schema => ({
        ...schema,
        examplePayload:
          schema.examplePayload && schema.examplePayload !== ''
            ? Base64.decode(schema.examplePayload)
            : schema.examplePayload
      }));
    }
    yield put(updateTotalCount(currentEnrichedSchemaList.length));
    yield put(
      actions.gettingEnrichedSchemasSucceeded(currentEnrichedSchemaList)
    );
  } catch (ex) {
    yield put(updateTotalCount(0));
    yield put(actions.gettingEnrichedSchemasFailed(ex));
  }
}

export function* getPaginatedEnrichedSchemasIfNeeded(action) {
  const { payload = {} } = action;
  try {
    let tenantId = yield select(currentTenantId);
    let businessContextId = yield select(currentBusinessContextId);
    let currentPaginatedSchemaList = yield select(paginatedSchemaList);
    yield call(waitForGetSupportedEventTypes);
    let productCategory = yield select(productCategories);
    let productName = yield select(productNames);

    if (
      action.type === actions.GET_PAGINATED_ENRICHED_SCHEMAS &&
      (currentPaginatedSchemaList.length === 0 || payload.force === true)
    ) {
      const requestBody = {
        name: yield select(searchInput),
        sortBy: yield select(sortBy),
        sortOrder: yield select(sortOrder),
        active: yield select(displayActive),
        productName: yield select(filterProductNames),
        productCategory: yield select(filterProductCategories)
      };
      let urlParams = {
        index: yield select(pageNo),
        pageSize: yield select(rowsPerPage)
      };
      let bodyParams = {
        ...getParams(requestBody, productName, productCategory)
      };
      let listCount = yield select(totalCount);
      if (
        isArchiveInProgress &&
        listCount - 1 &&
        urlParams.index >= (listCount - 1) / urlParams.pageSize
      ) {
        urlParams.index = urlParams.index - 1;
        yield put(updateEnrichedSchemaFilters({ pageNo: urlParams.index }));
      }
      currentPaginatedSchemaList = yield call(
        getPaginatedEnrichedSchemasAsync,
        tenantId,
        businessContextId,
        urlParams,
        bodyParams
      );
      yield put(updateEnrichedSchemaFilters({ totalCount: currentPaginatedSchemaList.totalRecords }));
      currentPaginatedSchemaList = produce(currentPaginatedSchemaList.data, draftList => {
        draftList = draftList.map(decodeExampleContent);
      });
    }
    isArchiveInProgress = false;
    yield put(actions.gettingPaginatedEnrichedSchemasSucceeded(currentPaginatedSchemaList));
  } catch (ex) {
    isArchiveInProgress = false;
    yield put(updateEnrichedSchemaFilters({ totalCount: 0 }));
    yield put(actions.gettingPaginatedEnrichedSchemasFailed(ex));
  }
}

export function* getEnrichedSchemaKeys() {
  try {
    let tenantId = yield select(currentTenantId);
    let businessContextId = yield select(currentBusinessContextId);
    let schemaKeys = yield call(
      getEnrichedSchemaKeysAsync,
      tenantId,
      businessContextId
    );
    yield put(actions.gettingEnrichedSchemaKeysSucceeded(schemaKeys));
  } catch (ex) {
    yield put(actions.gettingEnrichedSchemaKeysFailed(ex));
  }
}

export function* getEnrichedSchemaIfNeeded(action) {
  const { payload } = action;
  try {
    let tenantId = yield select(currentTenantId);
    let businessContextId = yield select(currentBusinessContextId);
    let schema = yield select(currentEnrichedSchema);
    let schemaId = payload.schemaId;
    if (
      !schema ||
      (schema && schema.uuid !== schemaId) ||
      payload.force === true
    ) {
      schema = yield call(
        getEnrichedSchemaAsync,
        tenantId,
        businessContextId,
        schemaId
      );
    }
    yield put(actions.gettingEnrichedSchemaSucceeded(schema));
  } catch (ex) {
    yield put(actions.gettingEnrichedSchemaFailed(ex));
  }
}

export function* createEnrichedSchemaFromCurrent() {
  try {
    let tenantId = yield select(currentTenantId);
    let businessContextId = yield select(currentBusinessContextId);
    let schema = yield select(currentEnrichedSchema);
    if (!schema.uuid) {
      schema = yield call(
        createEnrichedSchemaAsync,
        tenantId,
        businessContextId,
        schema
      );
    }
    yield put(actions.creatingEnrichedSchemaSucceeded(schema));
  } catch (ex) {
    yield put(
      actions.creatingEnrichedSchemaFailed({ isValidationFailed: false })
    );
  }
}

export function* updateEnrichedSchemaFromCurrent(action) {
  try {
    let tenantId = yield select(currentTenantId);
    let businessContextId = yield select(currentBusinessContextId);
    let schema = yield select(currentEnrichedSchema);
    schema = yield call(
      updateEnrichedSchemaAsync,
      tenantId,
      businessContextId,
      schema,
      action.payload.schemaId
    );
    if (!schema.uuid) {
      schema = { ...schema, id: action.payload.schemaId };
    }
    yield put(actions.updatingEnrichedSchemaSucceeded(schema));
  } catch (ex) {
    yield put(
      actions.updatingEnrichedSchemaFailed({ isValidationFailed: false })
    );
  }
}

export function* activateOrDeactivateEnrichedSchema(action) {
  let { payload } = action;
  try {
    let tenantId = yield select(currentTenantId);
    let businessContextId = yield select(currentBusinessContextId);
    let success = false;
    if (payload.activeStatus) {
      success = yield call(
        deactivateEnrichedSchemaAsync,
        tenantId,
        businessContextId,
        payload.schemaId
      );
    } else {
      success = yield call(
        activateEnrichedSchemaAsync,
        tenantId,
        businessContextId,
        payload.schemaId
      );
    }
    isArchiveInProgress = true;
    yield put(actions.activateOrDeactivateEnrichedSchemaSucceeded(success));
  } catch (ex) {
    isArchiveInProgress = false;
    yield put(
      actions.activateOrDeactivateEnrichedSchemaFailed({
        activeStatus: payload.activeStatus
      })
    );
  }
}
